import React, { useState, useEffect, useCallback } from 'react';
import FarmAssetsModal from './FarmAssetsModal';

// Map keys to backend URL segments
const URL_MAP = {
  farmAnimal: 'farm-animals',
  crop: 'crops',
  farmEquipment: 'farm-equipments',
  suppliesFeed: 'supplies-feed',
  otherFarmProperty: 'other-farm-properties',
};

// Define labels for each asset type
const assetTypes = [
  { key: 'farmAnimal', label: 'Farm Animals' },
  { key: 'crop', label: 'Crops' },
  { key: 'farmEquipment', label: 'Farm Equipment' },
  { key: 'suppliesFeed', label: 'Supplies & Feed' },
  { key: 'otherFarmProperty', label: 'Other Farm Property' },
];

// Map component keys to combined-view JSON keys
const jsonKeyMap = {
  farmAnimal: 'farm_animals',
  crop: 'crops',
  farmEquipment: 'farm_equipments',
  suppliesFeed: 'supplies_feed',
  otherFarmProperty: 'other_farm_properties',
};

export default function FarmAssetsSection({ caseId, exemptions, scope, onChange, onSave }) {
  const [assets, setAssets] = useState({});
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [selectedType, setSelectedType] = useState(assetTypes[0].key);

  // Fetch all farm assets in one call
  useEffect(() => {
    (async () => {
      const res = await fetch(
        `${process.env.REACT_APP_API_URL}/case/${caseId}/farm-assets/`,
        {
          headers: { Authorization: `Bearer ${localStorage.getItem('accessToken')}` },
        }
      );
      if (!res.ok) return;
      const json = await res.json();
      const farm = json.farm || {};
      const mapped = {};
      assetTypes.forEach(({ key }) => {
        mapped[key] = farm[jsonKeyMap[key]] || [];
      });
      setAssets(mapped);
      onChange && onChange(mapped);
    })();
  }, [caseId]);

  const handleDelete = useCallback(async (key, id) => {
    const urlType = URL_MAP[key];
    const res = await fetch(
      `${process.env.REACT_APP_API_URL}/case/${caseId}/${urlType}/${id}/delete/`,
      { method: 'DELETE', headers: { Authorization: `Bearer ${localStorage.getItem('accessToken')}` } }
    );
    if (res.ok) {
      setAssets(prev => {
        const updated = prev[key].filter(i => i.id !== id);
        const newAssets = { ...prev, [key]: updated };
        onChange && onChange(newAssets);
        return newAssets;
      });
    } else {
      alert('Failed to delete');
    }
  }, [caseId, onChange]);

  const handleSave = item => {
    const key = item.urlKey;
    setAssets(prev => {
      const list = prev[key] || [];
      const exists = list.find(i => i.id === item.id);
      const updated = exists
        ? list.map(i => (i.id === item.id ? item : i))
        : [...list, item];
      const newAssets = { ...prev, [key]: updated };
      onChange && onChange(newAssets);
      onSave && onSave(newAssets);
      return newAssets;
    });
    setIsModalOpen(false);
    setEditingItem(null);
  };

  return (
    <section className="mt-12 space-y-8">
      <header className="flex items-center justify-between bg-gray-100 p-4 rounded-md">
        <h2 className="text-2xl font-semibold">Farm Assets</h2>
        <div className="flex space-x-2">
          <select
            value={selectedType}
            onChange={e => setSelectedType(e.target.value)}
            className="border border-gray-300 rounded-md p-2"
          >
            <option value="">Select Asset…</option>
            {assetTypes.map(at => (
              <option key={at.key} value={at.key}>{at.label}</option>
            ))}
          </select>
          <button
            onClick={() => { setSelectedType(selectedType); setEditingItem(null); setIsModalOpen(true); }}
            disabled={!selectedType}
            className="px-4 py-2 bg-blue-600 text-white rounded-md disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Add Asset
          </button>
        </div>
      </header>

      {assetTypes
        .filter(({ key }) => (assets[key] || []).length > 0)
        .map(({ key, label }) => (
          <section key={key} className="mt-6">
            <h3 className="text-xl font-semibold mb-3">{label}</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {(assets[key] || []).map(item => (
                <div
                  key={item.id}
                  className="bg-white rounded-lg shadow p-6 flex flex-col hover:shadow-lg transition-shadow"
                >
                  <ul className="space-y-1 text-sm text-gray-700 mb-4 flex-grow">
                    {Object.entries(item)
                      .filter(([f]) => !['id','case','urlKey'].includes(f))
                      .map(([f, v]) => (
                        <li key={f}>
                          <strong>{f.replace(/_/g,' ').replace(/\b\w/g,c=>c.toUpperCase())}:</strong> {v}
                        </li>
                      ))}
                  </ul>
                  <div className="mt-auto flex space-x-2">
                    <button
                      onClick={() => { setSelectedType(key); setEditingItem({ ...item, urlKey: key }); setIsModalOpen(true); }}
                      className="px-3 py-1 bg-yellow-500 text-white rounded"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => handleDelete(key, item.id)}
                      className="px-3 py-1 bg-red-600 text-white rounded"
                    >
                      Delete
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </section>
      ))}

      <FarmAssetsModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        caseId={caseId}
        item={editingItem}
        onSave={handleSave}
        exemptions={exemptions}
        scope={scope}
        type={selectedType}
      />
    </section>
  );
}